import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import {
    Box,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popover,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'
import BrandLogo from '@/components/BrandLogo'
import SetupNavItem from '@/features/dashboard/setup/SetupNavItem'
import SettingsDrawer from '@/features/settings-drawer/SettingsDrawer'
import NGLogo from '@/features/themedLogos/NGLogo'
import useAuth from '@/hooks/useAuth'
import { navSections, settingsItems, topLevelItems } from '@/navConfig'
import { hasRole } from '@/utils/hasRole'
import { CreateModal } from '../app-top-bar/CreateModal'
import { canAccessNavEntry, canAccessNavSection } from '../nav-access'
import { SidebarItem } from './SidebarItem'
import { SidebarSection } from './SidebarSection'

const SIDEBAR_WIDTH = 280
const SIDEBAR_COLLAPSED_WIDTH = 64
const STORAGE_KEY = 'nav_sidebar_collapsed'

function getInitialCollapsed() {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored !== null) return stored === 'true'
    // Default: tablet (md < lg) collapses, desktop expands
    return window.innerWidth < 1200
}

function matchesNavItemPath(item, pathname) {
    return !!matchPath(
        {
            path: item.path,
            end: item.matchSubpaths === false || item.path === '/'
        },
        pathname
    )
}

function matchesNavSectionPath(section, pathname) {
    const sectionBasePath = section.discoverPath

    if (
        sectionBasePath !== '/' &&
        (pathname === sectionBasePath ||
            pathname.startsWith(`${sectionBasePath}/`))
    ) {
        return true
    }

    return section.items.some((item) => matchesNavItemPath(item, pathname))
}

export function AppSidebar() {
    const { isAuthenticated, user, activeOrg } = useAuth()
    const location = useLocation()
    const isStaff = user?.isStaff === true
    // Docs are gated to site admins for now, so only show the Help link to them.
    const isSiteAdmin = !!user && hasRole(user, ['Site Admin Role'])
    const [collapsed, setCollapsed] = useState(getInitialCollapsed)
    const [openSections, setOpenSections] = useState({ power: true })
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [settingsExpanded, setSettingsExpanded] = useState(false)
    const [settingsPopoutAnchor, setSettingsPopoutAnchor] = useState(null)
    const settingsIconRef = useRef(null)
    const settingsCloseTimer = useRef(null)

    const scheduleSettingsClose = () => {
        settingsCloseTimer.current = setTimeout(
            () => setSettingsPopoutAnchor(null),
            200
        )
    }
    const cancelSettingsClose = () => clearTimeout(settingsCloseTimer.current)
    const [createModalPrefix, setCreateModalPrefix] = useState(null)
    const belowMd = useMediaQuery((theme) => theme.breakpoints.down('md'))
    const effectiveCollapsed = collapsed || belowMd
    const navAccess = useMemo(
        () => ({ isAuthenticated, isStaff, user, activeOrg }),
        [isAuthenticated, isStaff, user, activeOrg]
    )
    // org-sidebar-logo D7: only the authenticated shell passes org context
    // into BrandLogo, and only once the org actually has a logo/mark set —
    // an org with neither falls through to BrandLogo's own brand/bolt
    // fallback exactly as before.
    const orgLogo = useMemo(() => {
        if (!activeOrg?.logo_url && !activeOrg?.logo_mark_url) return undefined
        return {
            logoUrl: activeOrg.logo_url || '',
            markUrl: activeOrg.logo_mark_url || '',
            name: activeOrg.name || ''
        }
    }, [activeOrg])
    const visibleSections = useMemo(
        () =>
            navSections.filter((section) =>
                canAccessNavSection(section, navAccess)
            ),
        [navAccess]
    )
    const visiblePinnedSections = visibleSections.filter(
        (section) => section.pinToTop
    )
    const visibleStandardSections = visibleSections.filter(
        (section) => !section.pinToTop
    )
    const visibleSettingsItems = settingsItems.filter((item) =>
        canAccessNavEntry(item, navAccess)
    )
    const activeSectionIds = useMemo(
        () =>
            visibleSections
                .filter((section) =>
                    matchesNavSectionPath(section, location.pathname)
                )
                .map((section) => section.id),
        [location.pathname, visibleSections]
    )

    useEffect(() => {
        if (activeSectionIds.length === 0) return

        setOpenSections((prev) => {
            let changed = false
            const next = { ...prev }

            activeSectionIds.forEach((sectionId) => {
                if (!next[sectionId]) {
                    next[sectionId] = true
                    changed = true
                }
            })

            return changed ? next : prev
        })
    }, [activeSectionIds])

    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            const next = !prev
            localStorage.setItem(STORAGE_KEY, String(next))
            return next
        })
    }

    const toggleSection = (sectionId) => {
        setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }))
    }

    const width = effectiveCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH

    return (
        <Drawer
            variant="permanent"
            sx={{
                // On a landscape phone (sm) the sidebar is flush with the
                // notch side; widen it by the inset and pad the paper so the
                // nav keeps its full width. env() is 0 everywhere else.
                width: `calc(${width}px + env(safe-area-inset-left, 0px))`,
                flexShrink: 0,
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': {
                    width: `calc(${width}px + env(safe-area-inset-left, 0px))`,
                    paddingLeft: 'env(safe-area-inset-left, 0px)',
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    transition: 'width 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Skip nav link — visually hidden, appears on focus */}
            <a
                href="#main-content"
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: 'auto',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                }}
                onFocus={(e) => {
                    e.target.style.left = '8px'
                    e.target.style.top = '8px'
                    e.target.style.width = 'auto'
                    e.target.style.height = 'auto'
                    e.target.style.zIndex = '9999'
                }}
                onBlur={(e) => {
                    e.target.style.left = '-9999px'
                    e.target.style.top = 'auto'
                    e.target.style.width = '1px'
                    e.target.style.height = '1px'
                }}
            >
                Skip to main content
            </a>

            <Box
                component="nav"
                aria-label="Main navigation"
                sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
            >
                {/* Logo */}
                <Box
                    component={Link}
                    title={'Home'}
                    to="/"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0,
                        pt: 1,
                        pb: 0,
                        textDecoration: 'none',
                        width: '100%'
                    }}
                >
                    <BrandLogo
                        height={65}
                        compact={effectiveCollapsed}
                        orgLogo={orgLogo}
                        sx={
                            effectiveCollapsed ? { ml: -1, mt: 0.5 } : undefined
                        }
                    />
                </Box>

                {/* Collapse toggle */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: effectiveCollapsed
                            ? 'center'
                            : 'flex-end',
                        p: 0.5
                    }}
                >
                    <Tooltip
                        title={
                            effectiveCollapsed
                                ? 'Expand sidebar'
                                : 'Collapse sidebar'
                        }
                        placement="right"
                    >
                        <IconButton
                            onClick={toggleCollapsed}
                            size="small"
                            aria-label={
                                effectiveCollapsed
                                    ? 'Expand sidebar'
                                    : 'Collapse sidebar'
                            }
                        >
                            {effectiveCollapsed ? (
                                <ChevronRightIcon />
                            ) : (
                                <ChevronLeftIcon />
                            )}
                        </IconButton>
                    </Tooltip>
                </Box>

                <Divider />

                {visiblePinnedSections.map((section) => (
                    <SidebarSection
                        key={section.id}
                        section={section}
                        collapsed={effectiveCollapsed}
                        navAccess={navAccess}
                        open={!!openSections[section.id]}
                        onToggle={() => toggleSection(section.id)}
                        onCreateClick={(section) =>
                            setCreateModalPrefix(section.createPathPrefix)
                        }
                    />
                ))}

                {visiblePinnedSections.length > 0 && (
                    <Divider sx={{ my: 0.5 }} />
                )}

                {/* Top-level items */}
                <List disablePadding>
                    {topLevelItems
                        .filter((item) => canAccessNavEntry(item, navAccess))
                        .map((item) => (
                            <SidebarItem
                                key={item.id}
                                item={item}
                                collapsed={effectiveCollapsed}
                            />
                        ))}
                    <SetupNavItem collapsed={effectiveCollapsed} />
                </List>

                <Divider sx={{ my: 0.5 }} />

                {/* Collapsible sections */}
                {visibleStandardSections.map((section) => (
                    <SidebarSection
                        key={section.id}
                        section={section}
                        collapsed={effectiveCollapsed}
                        navAccess={navAccess}
                        open={!!openSections[section.id]}
                        onToggle={() => toggleSection(section.id)}
                        onCreateClick={(section) =>
                            setCreateModalPrefix(section.createPathPrefix)
                        }
                    />
                ))}
            </Box>

            {/* Pinned bottom — Help + Settings (collapses to icons) */}
            <Box>
                <Divider />
                <List disablePadding>
                    {isSiteAdmin && (
                        <Tooltip
                            title={effectiveCollapsed ? 'Help' : ''}
                            placement="right"
                        >
                            <ListItemButton
                                component={Link}
                                to="/docs/power/welcome"
                                sx={{
                                    minHeight: 48,
                                    px: 2.5,
                                    justifyContent: effectiveCollapsed
                                        ? 'center'
                                        : 'flex-start'
                                }}
                                aria-label="Help"
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: effectiveCollapsed ? 0 : 2,
                                        justifyContent: 'center'
                                    }}
                                >
                                    <HelpOutlineIcon />
                                </ListItemIcon>
                                {!effectiveCollapsed && (
                                    <ListItemText primary="Help" />
                                )}
                            </ListItemButton>
                        </Tooltip>
                    )}
                    <ListItemButton
                        ref={settingsIconRef}
                        onClick={() =>
                            !effectiveCollapsed &&
                            setSettingsExpanded((p) => !p)
                        }
                        onMouseEnter={() => {
                            if (effectiveCollapsed) {
                                cancelSettingsClose()
                                setSettingsPopoutAnchor(settingsIconRef.current)
                            }
                        }}
                        onMouseLeave={() => {
                            if (effectiveCollapsed) scheduleSettingsClose()
                        }}
                        sx={{
                            minHeight: 48,
                            px: 2.5,
                            justifyContent: effectiveCollapsed
                                ? 'center'
                                : 'flex-start'
                        }}
                        aria-label="Settings"
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: effectiveCollapsed ? 0 : 2,
                                justifyContent: 'center'
                            }}
                        >
                            <SettingsOutlinedIcon />
                        </ListItemIcon>
                        {!effectiveCollapsed && (
                            <>
                                <ListItemText primary="Settings" />
                                {settingsExpanded ? (
                                    <ExpandLessIcon />
                                ) : (
                                    <ExpandMoreIcon />
                                )}
                            </>
                        )}
                    </ListItemButton>
                    <Popover
                        open={!!settingsPopoutAnchor}
                        anchorEl={settingsPopoutAnchor}
                        onClose={() => setSettingsPopoutAnchor(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                        disableRestoreFocus
                        slotProps={{
                            root: { style: { pointerEvents: 'none' } },
                            paper: {
                                sx: { minWidth: 180, pointerEvents: 'auto' },
                                onMouseEnter: cancelSettingsClose,
                                onMouseLeave: () =>
                                    setSettingsPopoutAnchor(null)
                            }
                        }}
                    >
                        <Paper elevation={0}>
                            <Typography
                                variant="overline"
                                sx={{
                                    px: 2,
                                    pt: 1,
                                    display: 'block',
                                    color: 'text.secondary'
                                }}
                            >
                                Settings
                            </Typography>
                            <Divider />
                            <List dense disablePadding>
                                {visibleSettingsItems.map((item) => (
                                    <ListItemButton
                                        key={item.id}
                                        component={Link}
                                        to={item.path}
                                        onClick={() =>
                                            setSettingsPopoutAnchor(null)
                                        }
                                    >
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.label} />
                                    </ListItemButton>
                                ))}
                                <ListItemButton
                                    onClick={() => {
                                        setSettingsPopoutAnchor(null)
                                        setSettingsOpen(true)
                                    }}
                                >
                                    <ListItemIcon>
                                        <TuneIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Display" />
                                </ListItemButton>
                            </List>
                        </Paper>
                    </Popover>
                    <Collapse in={!effectiveCollapsed && settingsExpanded}>
                        <List disablePadding>
                            {visibleSettingsItems.map((item) => (
                                <SidebarItem
                                    key={item.id}
                                    item={item}
                                    collapsed={false}
                                    sx={{ pl: 4 }}
                                />
                            ))}
                            <ListItemButton
                                onClick={() => setSettingsOpen(true)}
                                sx={{
                                    minHeight: 48,
                                    px: 2.5,
                                    pl: 4
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: 2,
                                        justifyContent: 'center'
                                    }}
                                >
                                    <TuneIcon />
                                </ListItemIcon>
                                <ListItemText primary="Display" />
                            </ListItemButton>
                        </List>
                    </Collapse>
                </List>
            </Box>

            <SettingsDrawer
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
            />
            <CreateModal
                open={!!createModalPrefix}
                onClose={() => setCreateModalPrefix(null)}
                pathPrefix={createModalPrefix}
            />
        </Drawer>
    )
}
